// 214 §2–§4, §6 [RULED 51–53] — THE PURE HALF OF THE COUNSEL LINKS. How a link's parties and state are read off a
// regular case's wire (`ocuLinks` / `cpLinks`, maps beside `ocuRoles` / `clLinks`, server/src/lib/shapes.js) and
// off a collective case's `ocuIds` elements ({ id, roles, partyIds?, status?, suspendedDate? }); how a contact is
// known as a client's own counsel (the `contact_role` code `lawyer`); how the chips, the sublists and the records
// order and word a state. No React, no fetch, no `any` — every reader of these facts goes through here, so the
// drive's recording and the two views cannot disagree.

export const SUSPENDED = "Suspended";
/** The `contact_role` code that makes a client's contact that client's own counsel (PARTIAL kind, protected). */
export const CONTACT_ROLE_LAWYER = "lawyer";
/** What a NULL `ContactPerson.role` reads as (never compared — the fallback word of the picker and the record). */
export const CONTACT_ROLE_DEFAULT = "contact";

export interface LinkState {
  status?: string | null;
  suspendedDate?: string | null;
}
export interface CounselLink extends LinkState {
  partyIds?: readonly unknown[] | null;
}
export type LinkMap<T> = Readonly<Record<string, T | null | undefined>> | null | undefined;

// the callbacks' shapes, by type witness (src/lib/fnTypes.ts says why)
export type { IdToNode, IdToBool, SetRowsFn, NavFn, LpFn } from "./fnTypes";

export function linkOf<T>(map: LinkMap<T>, id: number | string): T | null {
  if (!map || typeof map !== "object") return null;
  return map[String(id)] ?? null;
}

export function isSuspended(lk: LinkState | null | undefined): boolean {
  return !!lk && lk.status === SUSPENDED;
}

export function suspendedDateOf(lk: LinkState | null | undefined): string {
  return lk && typeof lk.suspendedDate === "string" ? lk.suspendedDate : "";
}

export function partyIdsOf(lk: CounselLink | null | undefined): number[] {
  const ids = lk?.partyIds;
  if (!Array.isArray(ids)) return [];
  return ids.map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

/** A contact row's role code, `contact` when none is stored. */
export function roleCodeOf(p: { role?: unknown } | null | undefined): string {
  return p && typeof p.role === "string" && p.role.trim() ? p.role : CONTACT_ROLE_DEFAULT;
}

/** Is this contact its client's own counsel (joint representation)? The literal is deliberate: it is the binding
 *  `scripts/lint/taxonomy-classification.js` attributes to the PARTIAL kind's protected code. */
export function isClientCounsel(p: { role?: unknown } | null | undefined): boolean {
  return !!p && p.role === "lawyer";
}

/** The ids in their given order, the acting ones first and the suspended ones (by `links`) after them. */
export function actingFirst(ids: readonly number[], links: LinkMap<LinkState>): number[] {
  const acting: number[] = [];
  const suspended: number[] = [];
  for (const id of ids) (isSuspended(linkOf(links, id)) ? suspended : acting).push(id);
  return [...acting, ...suspended];
}

/** `ids` with `id` added or removed (a checkbox list's toggle). */
export function toggleId(ids: unknown, id: number): number[] {
  const cur = Array.isArray(ids) ? ids.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];
  return cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
}

/** The counsel ids (of `ocuIds`) whose link names `partyId` among its parties. */
export function counselOfParty(ocuIds: readonly unknown[] | null | undefined, links: LinkMap<CounselLink>, partyId: number): number[] {
  const out: number[] = [];
  for (const raw of ocuIds ?? []) {
    const id = Number(raw);
    if (!Number.isFinite(id)) continue;
    if (partyIdsOf(linkOf(links, id)).includes(partyId)) out.push(id);
  }
  return out;
}

// ── a collective case's elements ─────────────────────────────────────────────────────────────────────────────────
export interface CcElement extends CounselLink {
  id: number;
  roles?: readonly string[];
}

function asElement(x: unknown): CcElement | null {
  if (typeof x === "number") return Number.isFinite(x) ? { id: x } : null;
  if (typeof x === "object" && x !== null && !Array.isArray(x)) {
    const id = Number((x as { id?: unknown }).id);
    return Number.isFinite(id) ? { ...x, id } : null;
  }
  return null;
}

/** The element of `list` (a cc's `ocuIds`) carrying `id`, or null. */
export function ccElementOf(list: unknown, id: number): CcElement | null {
  const src: readonly unknown[] = Array.isArray(list) ? list : [];
  for (const x of src) {
    const e = asElement(x);
    if (e?.id === id) return e;
  }
  return null;
}

/** The cc's `ocuIds` as a links map — the same shape a regular case's `ocuLinks` has, so one reader serves both. */
export function ccLinksOf(list: unknown): Record<string, CounselLink> {
  const out: Record<string, CounselLink> = {};
  const src: readonly unknown[] = Array.isArray(list) ? list : [];
  for (const x of src) {
    const e = asElement(x);
    if (e) out[String(e.id)] = { partyIds: e.partyIds ?? [], status: e.status ?? null, suspendedDate: e.suspendedDate ?? null };
  }
  return out;
}

/** A new list with the one element patched — every other element verbatim (a number element becomes `{ id }`
 *  only when it is the one patched; the server's validation takes objects with a numeric `id`). */
export function ccElementsWith(list: unknown, id: number, patch: Partial<CcElement>): unknown[] {
  const src: readonly unknown[] = Array.isArray(list) ? list : [];
  return src.map((x) => {
    const e = asElement(x);
    if (e?.id !== id) return x;
    return { ...e, ...patch, id };
  });
}

// ── the dates ────────────────────────────────────────────────────────────────────────────────────────────────────
/** `YYYY-MM-DD` → « JJ/MM/AA » (the brief's « suspendu depuis le JJ/MM/AA »); anything else as it is. */
export function shortDate(iso: string | null | undefined): string {
  const s = iso ?? "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : s;
}

/** Today in Brussels, `YYYY-MM-DD` (the suspend window's default). */
export function todayIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Brussels", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** 214 addendum 3 §21 [ADVISOR — one helper for every element-shaped column, named for what it does] — THE IDS OF A
 *  PARTY COLUMN, as numbers: a regular case's (number[]) or a collective case's elements' (`opIds`, `ocuIds`,
 *  `op2Ids` — `{ id, roles, … }`). The export's cell; `join` over the elements wrote « [object Object] ». */
export function elementIdsOf(list: unknown): number[] {
  const src: readonly unknown[] = Array.isArray(list) ? list : [];
  return src.map((x) => asElement(x)).filter((e): e is CcElement => !!e).map((e) => e.id);
}
/** The counsel ids of a case or of a cc's elements — `elementIdsOf` under the name 214 §2 gave it. */
export const counselIdsOf = elementIdsOf;

/** The opposing parties' ids of a matter — a regular case's `opIds` (ids) or a collective case's (elements). */
export function opponentIdsOfMatter(matter: unknown): number[] {
  if (typeof matter !== "object" || matter === null) return [];
  return elementIdsOf((matter as { opIds?: unknown }).opIds);
}

/** A counsel added from a regular case's quick add: the row with the new link's parties seated in `ocuLinks`
 *  (acting, no date). Any other family: the row as it is. Takes unknowns so the caller's `any` row adds no
 *  unsafe read (App.tsx's ESLint count may not rise). */
export function withNewCounselLink(row: unknown, retKey: unknown, id: unknown, partyIds: readonly number[]): unknown {
  if (retKey !== "ocuIds" || typeof row !== "object" || row === null) return row;
  const n = Number(id);
  if (!Number.isFinite(n)) return row;
  const prev = (row as { ocuLinks?: unknown }).ocuLinks;
  const links: Record<string, unknown> = typeof prev === "object" && prev !== null ? { ...prev } : {};
  links[String(n)] = { partyIds: [...partyIds], status: null, suspendedDate: null };
  return { ...row, ocuLinks: links };
}

/** A counsel added from a collective case's quick add: the new `ocuIds` element, its parties on it. */
export function newCcElement(id: unknown, retKey: unknown, partyIds: readonly number[]): { id: unknown; roles: string[]; partyIds?: number[] } {
  return retKey === "ocuIds" ? { id, roles: [], partyIds: [...partyIds] } : { id, roles: [] };
}

/** A new counsel element, as the import creates one: acting, no role, no party (`newCcElement`'s shape). */
export const freshCounselElement = (id: number): CcElement => ({ id, roles: [], partyIds: [] });
/** 214 addendum 3 §21 — a new opponent or additional-opponent element: no role (`CC_OBJ_PARTY_COLS`' shape,
 *  server/src/routes/collectiveCases.js — `{ id, roles }`; a party element carries no `partyIds`). */
export const freshPartyElement = (id: number): CcElement => ({ id, roles: [] });

/** 214 addendum 2 §18 [ADVISOR — 213B's merge rule] — a collective case's elements after a sheet names `ids`:
 *  an id with a stored element keeps that element VERBATIM (roles, parties, state, date); an id the sheet drops
 *  leaves; a new id enters as `fresh` makes it — a counsel acting with no party (the default), an opponent with no
 *  role (addendum 3 §21: the same merge for `opIds` and `op2Ids`). The sheet's order; a repeated id once. */
export function mergeCcElements(list: unknown, ids: readonly number[], fresh: typeof freshCounselElement = freshCounselElement): CcElement[] {
  const out: CcElement[] = [];
  for (const id of ids) {
    if (!Number.isFinite(id) || out.some((e) => e.id === id)) continue;
    out.push(ccElementOf(list, id) ?? fresh(id));
  }
  return out;
}

// ── a collective case's members (`clientLinks`, keyed by `clId`) ────────────────────────────────────────────────
/** A member's element: the client's id and whatever the roster keeps on the link (status, payments, dues, notes,
 *  dates, quantum, numbers, marks — server/src/lib/ccRoster.js's columns and the element's other keys). */
export interface CcMember {
  clId: number;
  roles?: readonly string[];
  [key: string]: unknown;
}

function asMember(x: unknown): CcMember | null {
  if (typeof x !== "object" || x === null || Array.isArray(x)) return null;
  const clId = Number((x as { clId?: unknown }).clId);
  return Number.isFinite(clId) ? { ...(x as Record<string, unknown>), clId } : null;
}

/** The member element of `links` (a cc's `clientLinks`) carrying `clId`, or null. */
export function ccMemberOf(links: unknown, clId: number): CcMember | null {
  const src: readonly unknown[] = Array.isArray(links) ? links : [];
  for (const x of src) {
    const m = asMember(x);
    if (m?.clId === clId) return m;
  }
  return null;
}

/** 214 addendum 4 §26 [RULED 64 — Laurent, 04/10] — THE COLLECTIVE LIST ADDS MEMBERS AND NEVER REMOVES ONE. A
 *  collective case's members after the list sheet names `clIds` (its `ClientIds` cell, or the ids of a legacy
 *  `ClientLinks` column): every stored member stays VERBATIM, named or not — his payments, stated dues and their
 *  notes, status, dates, quantum, numbers —, and each named id the case lacks enters as the add window makes a
 *  member with no date and no payment (`{ clId, status: "Active", payments: [], roles: [] }`) [ADVISOR]. A member
 *  leaves a collective case only from the case itself. Until this (addendum 3 §21's `mergeCcMembers`) a member the
 *  cell did not name LEFT — an emptied cell took all of them: staging's 305 on 04/10 (docs/214-deploy-note.md
 *  §7.27). The stored order, then the sheet's for the new ones; a repeated id once. */
export function ccMembersAdded(links: unknown, clIds: readonly number[]): CcMember[] {
  const out: CcMember[] = [];
  for (const x of Array.isArray(links) ? links : []) {
    const m = asMember(x);
    if (m && !out.some((o) => o.clId === m.clId)) out.push(m);
  }
  for (const clId of clIds) {
    if (!Number.isFinite(clId) || out.some((m) => m.clId === clId)) continue;
    out.push({ clId, status: "Active", payments: [], roles: [] });
  }
  return out;
}
